import { NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';

import { LottieComponent, type AnimationOptions } from 'ngx-lottie';

import { UiBrandingService } from '@core/services/ui-branding.service';

import { Dynamic } from '@shared/registry';

import type { OverlaySplashBranding, OverlaySplashConfig, OverlaySplashVariant } from './overlay-splash.types';

@Component({
  selector: 'app-overlay-splash',
  templateUrl: './overlay-splash.html',
  imports: [NgStyle, LottieComponent, Dynamic],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'h-full' },
})
export class OverlaySplash {
  private readonly brandingService = inject(UiBrandingService);

  readonly config = input<OverlaySplashConfig>({});
  readonly variant = input<OverlaySplashVariant>('default');

  private readonly brandingKey = computed(() => (this.variant() === 'entry' ? 'entrysplash' : 'overlaysplash'));

  private readonly liveBrandingConfig = computed(() => {
    const branding = this.brandingService.branding();
    const components = branding?.['components'] as Record<string, unknown> | undefined;
    return components?.[this.brandingKey()] as OverlaySplashBranding | undefined;
  });

  // For the entry variant, latch the branding config once to prevent animation restarts
  // when the theme is re-applied (public theme → authenticated theme).
  private readonly latchedEntryConfig = signal<OverlaySplashBranding | undefined>(undefined);

  private readonly brandingConfig = computed(() => {
    if (this.isEntryVariant()) return this.latchedEntryConfig();
    return this.liveBrandingConfig();
  });

  protected readonly isEntryVariant = computed(() => this.variant() === 'entry');
  protected readonly lottieUrl = computed(() => this.brandingConfig()?.lottieUrl);

  protected readonly containerStyle = computed(() => {
    if (!this.isEntryVariant()) return null;
    const config = this.brandingConfig();
    const bgColor = config?.backgroundColor ?? 'var(--page-background)';
    const url = config?.backgroundUrl;
    if (!url) return { backgroundColor: bgColor };
    return {
      backgroundColor: bgColor,
      backgroundImage: `url(${url})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    };
  });

  protected readonly lottieOptions = computed<AnimationOptions>(() => ({
    path: this.lottieUrl(),
    loop: this.brandingConfig()?.loop ?? true,
    autoplay: true,
  }));

  constructor() {
    // Latch the first non-undefined branding config for the entry splash
    effect(() => {
      const config = this.liveBrandingConfig();
      if (config && !this.latchedEntryConfig()) {
        this.latchedEntryConfig.set(config);
      }
    });
  }
}
